import { Box, InputAdornment } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";
import { DataGrid, TextField, DatePicker } from "../../common";
import moment from "moment";
import { OneOffInvoiceItem, CreateInvoiceDatagridRef } from "../../../types";
import { CreateOneOffInvoiceSchema } from "../../../validations/schema";
import { formatCurrency, theme } from "../../../utils";
import { z } from "zod";
import { useEffect, forwardRef, useImperativeHandle } from "react";
import { ActionIconsV2 } from "../../common";
import { AdjustmentType } from "../../../enums";

type Props = {
  onTotalChange?: (total: number) => void;
  onAdjustmentChange?: (adjustments: {
    discount: number;
    fee: number;
    discountPercent: number;
  }) => void;
  totalInvoiceAmount: number;
  loading?: boolean;
};
export const CreateInvoiceDatagrid = forwardRef<
  CreateInvoiceDatagridRef,
  Props
>(({ onTotalChange, onAdjustmentChange, totalInvoiceAmount, loading }, ref) => {
  const [rows, setRows] = useState<OneOffInvoiceItem[]>([
    {
      id: 1,
      date: moment(),
      service: "",
      unitPrice: "",
      quantity: "",
      total: 0,
      isAdjustment: false,
    },
  ]);

  const [rowErrors, setRowErrors] = useState<{
    unitPrice: { [rowId: number]: string };
    quantity: { [rowId: number]: string };
  }>({
    unitPrice: {},
    quantity: {},
  });
  useEffect(() => {
    const total = rows
      .filter((row) => !row.isAdjustment)
      .reduce((sum, row) => sum + Number(row.total || 0), 0);

    onTotalChange?.(total);
  }, [rows]);
  useEffect(() => {
    let discount = 0;
    let fee = 0;
    let discountPercent = 0;

    rows
      .filter((row) => row.isAdjustment)
      .forEach((row) => {
        const value = Number(row.total || 0);

        if (row.adjustmentType === AdjustmentType.invoiceDiscount) {
          discount += value;
        } else if (row.adjustmentType === AdjustmentType.invoiceFee) {
          fee += value;
        }
      });

    onAdjustmentChange?.({
      discount,
      fee,
      discountPercent,
    });
  }, [rows]);

  const handleAddItem = () => {
    const newId = Math.max(...rows.map((r) => r.id)) + 1;

    setRows((prev) => [
      ...prev,
      {
        id: newId,
        date: moment(),
        service: "",
        unitPrice: "",
        quantity: "",
        total: 0,
        isAdjustment: false,
      },
    ]);
  };
  const addAdjustmentRow = (adjustment: {
    label: string;
    amount: number;
    note?: string;
    isPercentage?: boolean;
    displayValue?: string | number;
  }) => {
    setRows((prev) => {
      const newId = Math.max(...prev.map((r) => r.id)) + 1;

      return [
        ...prev,
        {
          id: newId,
          date: moment(),
          service: adjustment.note || adjustment.label,
          adjustmentType:
            adjustment.label === "Discount"
              ? AdjustmentType.invoiceDiscount
              : AdjustmentType.invoiceFee,
          unitPrice: adjustment.isPercentage
            ? adjustment.displayValue?.toString() || ""
            : Math.abs(adjustment.amount).toString(),

          isPercentage: adjustment.isPercentage,
          quantity: "1",
          total: adjustment.amount,
          isAdjustment: true,
        },
      ];
    });
  };
  const handleDeleteRow = (id: number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const handleProcessRowUpdate = (newRow: OneOffInvoiceItem) => {
    const total = calculateRowTotal(
      newRow,
      newRow.unitPrice || "0",
      newRow.quantity || "0",
      rows,
    );

    const updatedRow = { ...newRow, total };

    setRows((prev) =>
      prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)),
    );

    return updatedRow;
  };
  const checkValidation = (): boolean => {
    const newErrors: typeof rowErrors = {
      unitPrice: {},
      quantity: {},
    };

    let isValid = true;

    rows.forEach((row) => {
      try {
        CreateOneOffInvoiceSchema.parse({
          unitPrice: row.unitPrice,
          quantity: row.quantity,
        });
      } catch (e) {
        isValid = false;

        if (e instanceof z.ZodError) {
          e.errors.forEach((error) => {
            if (error.path.includes("unitPrice")) {
              newErrors.unitPrice[row.id] = error.message;
            }

            if (error.path.includes("quantity")) {
              newErrors.quantity[row.id] = error.message;
            }
          });
        }
      }
    });

    setRowErrors(newErrors);

    return isValid;
  };
  useImperativeHandle(ref, () => ({
    validate: checkValidation,
    addAdjustmentRow,
    getRows: () => rows,
    reset: () => {
      setRows([
        {
          id: 1,
          date: moment(),
          service: "",
          unitPrice: "",
          quantity: "",
          total: 0,
          isAdjustment: false,
        },
      ]);
    },
  }));
  const calculateRowTotal = (
    row: OneOffInvoiceItem,
    unitPrice: string,
    quantity: string,
    allRows: OneOffInvoiceItem[],
  ) => {
    let total = 0;

    if (row.isAdjustment && row.isPercentage) {
      const percent = Number(unitPrice || 0);

      const amount = (totalInvoiceAmount * percent) / 100;

      total = amount * Number(quantity || 0);

      total =
        row.adjustmentType === AdjustmentType.invoiceDiscount ? -total : total;
    } else {
      total = Number(unitPrice || 0) * Number(quantity || 0);
    }

    return total;
  };

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date",
      width: 180,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <DatePicker
            name={`date-${params.row.id}`}
            disabled={params.row.isAdjustment}
            label=""
            placeHolder=""
            defaultValue={params.row.date}
            onChange={(value) => {
              setRows((prev) =>
                prev.map((row) =>
                  row.id === params.row.id ? { ...row, date: value } : row,
                ),
              );
            }}
          />
        </Box>
      ),
    },
    {
      field: "service",
      headerName: "Service",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <TextField
            label=""
            name=""
            value={params.row.service}
            size="small"
            fullWidth
            sx={{ backgroundColor: "white" }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              e.stopPropagation();
              const value = e.target.value;

              setRows((prev) =>
                prev.map((row) =>
                  row.id === params.row.id ? { ...row, service: value } : row,
                ),
              );
            }}
          />
        </Box>
      ),
    },
    {
      field: "unitPrice",
      headerName: "Unit Price",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <TextField
            key={`${params.row.id}-${params.row.isPercentage}`}
            label=""
            name=""
            type="text"
            value={params.row.unitPrice}
            size="small"
            fullWidth
            sx={{
              backgroundColor: "white",
              "& .MuiInputBase-input": {
                paddingLeft: "0px",
                textAlign: "center",
              },
            }}
            error={
              rowErrors.unitPrice[params.row.id]
                ? {
                    type: "manual",
                    message: rowErrors.unitPrice[params.row.id],
                  }
                : undefined
            }
            slotProps={{
              input: {
                startAdornment:
                  !params.row.isAdjustment ||
                  (params.row.isAdjustment && !params.row.isPercentage) ? (
                    <InputAdornment position="start">$</InputAdornment>
                  ) : null,

                endAdornment:
                  params.row.isAdjustment && params.row.isPercentage ? (
                    <InputAdornment position="end">%</InputAdornment>
                  ) : null,
              },
            }}
            onChange={(e) => {
              const value = e.target.value;

              setRows((prev) =>
                prev.map((row) => {
                  if (row.id !== params.row.id) return row;

                  const total = calculateRowTotal(
                    row,
                    value,
                    row.quantity || "0",
                    prev,
                  );

                  return {
                    ...row,
                    unitPrice: value,
                    total,
                  };
                }),
              );
            }}
          />
        </Box>
      ),
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <TextField
            label=""
            name=""
            type="text"
            defaultValue={params.row.quantity}
            size="small"
            disabled={params.row.isAdjustment}
            fullWidth
            sx={{
              backgroundColor: "white",
              "& input": { textAlign: "center" },
            }}
            error={
              rowErrors.quantity[params.row.id]
                ? {
                    type: "manual",
                    message: rowErrors.quantity[params.row.id],
                  }
                : undefined
            }
            onChange={(e) => {
              const value = e.target.value;

              setRows((prev) =>
                prev.map((row) => {
                  if (row.id !== params.row.id) return row;

                  const total = calculateRowTotal(
                    row,
                    row.unitPrice || "0",
                    value,
                    prev,
                  );

                  return {
                    ...row,
                    quantity: value,
                    total,
                  };
                }),
              );
            }}
          />
        </Box>
      ),
    },
    {
      field: "total",
      headerName: "Total",
      width: 180,
      align: "center",
      headerAlign: "center",
      editable: false,
      renderCell: (params) => {
        const value = Number(params.row.total || 0);

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {params.row.adjustmentType === "Discount"
              ? `-${formatCurrency(value)}`
              : formatCurrency(value)}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const isDisabled = rows.length === 1;

        return (
          <Box
            sx={{
              pointerEvents: isDisabled ? "none" : "auto",
              opacity: isDisabled ? 0.4 : 1,
            }}
          >
            <ActionIconsV2
              onEdit={() => {}}
              onDelete={() => handleDeleteRow(params.row.id)}
              rowData={params.row}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ position: "relative", height: "70vh" }}>
      <Box sx={{ position: "absolute", inset: 0 }}>
        <DataGrid
          disableRowSelectionOnClick
          getRowHeight={(params) => {
            const id = Number(params.id);
            if (rowErrors.unitPrice[id] || rowErrors.quantity[id]) {
              return 80;
            }
            return 50;
          }}
          checkboxSelection={false}
          columns={columns}
          rows={rows}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          processRowUpdate={handleProcessRowUpdate}
          totalRecords={rows.length}
          loading={loading}
          sx={{
            backgroundColor: theme.palette.secondary.light,
            padding: "20px",
            paddingTop: "0px",
            borderBottomLeftRadius: "10px",
            borderBottomRightRadius: "10px",
            "& .MuiSvgIcon-root": {
              fontSize: 16,
            },
            height: "70vh",
            minHeight: "70vh",
          }}
          footerProps={{
            hasAddIcon: true,
            addBtnLabel: "Add Item",
            onAddButtonClick: handleAddItem,
          }}
        />
      </Box>
    </Box>
  );
});
